import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { CODES } from "../../consts/codes";
import { GetRates, ListDownloadRate } from "../../services/user/rateService";

/**
 * Slice de Redux para el estado de tarifas.
 */
export const rateSlice = createSlice({
  name: "rate",
  initialState: {
    rateLoad: [],
    rateLoadStatus: "fetch",
    rateDownload: [],
    rateDownloadStatus: "fetch",
    error: null,
  },
  reducers: {
    /**
     * Reinicia el estado de carga de tarifas a "fetch".
     * @param {Object} state - Estado actual del slice.
     * @param {Object} action - Acción de Redux.
     */
    resetRatesLoadState(state, action) {
      state.rateLoadStatus = "fetch";
    },
    /**
     * Reinicia el estado de descarga de tarifas a "fetch".
     * @param {Object} state - Estado actual del slice.
     * @param {Object} action - Acción de Redux.
     */
    resetRateDownloadState(state, action) {
      state.rateDownloadStatus = "fetch";
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchRateLoad.pending, (state, action) => {
        state.rateLoadStatus = "loading";
      })
      .addCase(fetchRateLoad.fulfilled, (state, action) => {
        state.rateLoadStatus = "succeeded";
        state.rateLoad = action.payload;
      })
      .addCase(fetchRateLoad.rejected, (state, action) => {
        state.rateLoadStatus = "failed";
        state.rateLoad = [];
      })
      .addCase(fetchRateDownload.pending, (state, action) => {
        state.rateDownloadStatus = "loading";
      })
      .addCase(fetchRateDownload.fulfilled, (state, action) => {
        state.rateDownloadStatus = "succeeded";
        state.rateDownload = action.payload;
      })
      .addCase(fetchRateDownload.rejected, (state, action) => {
        state.rateDownloadStatus = "failed";
      });
  },
});

/**
 * Obtiene el listado de tarifas cargadas.
 * @param {Object} state - Estado global de Redux.
 * @returns {Array} Listado de tarifas.
 */
export const getRateLoad = (state) => state.rate.rateLoad;
/**
 * Obtiene el estado de carga de tarifas.
 * @param {Object} state - Estado global de Redux.
 * @returns {string} Estado de carga ("fetch", "loading", "succeeded", "failed").
 */
export const getRateLoadStatus = (state) => state.rate.rateLoadStatus;

/**
 * Obtiene el listado de tarifas descargadas.
 * @param {Object} state - Estado global de Redux.
 * @returns {Array} Listado de tarifas descargadas.
 */
export const getRateDownload = (state) => state.rate.rateDownload;
/**
 * Obtiene el estado de descarga de tarifas.
 * @param {Object} state - Estado global de Redux.
 * @returns {string} Estado de descarga ("fetch", "loading", "succeeded", "failed").
 */
export const getRateDownloadStatus = (state) => state.rate.rateDownloadStatus;

export const { resetRatesLoadState, resetRateDownloadState } = rateSlice.actions;

/**
 * Obtiene el listado de tarifas desde el servidor.
 * @param {Object} data - Parámetros para la consulta de tarifas.
 * @returns {Promise<Array|undefined>} Listado de tarifas o undefined si falla.
 */
export const fetchRateLoad = createAsyncThunk("rate/fetchAllRateLoad", async (data) => {
  //TODO: Ajustar el objeto GetCatalogues por integracion de grupo empresarial
  const requestRateLoad = await GetRates({
    ...data,
  });

  if (
    requestRateLoad.status === CODES.COD_RESPONSE_SUCCESS &&
    requestRateLoad.data.responseCode === CODES.COD_RESPONSE_SUCCESS
  ) {
    return requestRateLoad.data.data;
  }
});

//TODO: en esta funcion ajuste el objecto para recibir el objecto para que funione como grupo empresarial
/**
 * Descarga el listado de tarifas desde el servidor.
 * @param {Object} data - Parámetros para la descarga de tarifas.
 * @returns {Promise<Array|undefined>} Listado de tarifas descargadas o undefined si falla.
 */
export const fetchRateDownload = createAsyncThunk("rate/fetchAllRateDownload", async (data) => {
  const requestRateDownload = await ListDownloadRate({
    ...data,
  });
  if (requestRateDownload.status === CODES.COD_RESPONSE_SUCCESS) {
    if (requestRateDownload.data.responseCode === CODES.COD_RESPONSE_SUCCESS) {
      return requestRateDownload.data.data;
    }
  }
});

/** El reducer del slice de tarifas. */
export default rateSlice.reducer;